import { applyChartFacts, mergePatientDemographics } from '../studies/applyChartFacts';

const CERVICAL_APPROACHES = {
  acdf: 'Anterior cervical discectomy and fusion (ACDF)',
  laminectomyFusion: 'Posterior cervical laminectomy and fusion',
  posterior: 'Posterior cervical fusion',
  occiput: 'Occipito-cervical fusion',
  other: 'Other',
};

const TOBACCO = {
  never: 'Never used tobacco',
  former: 'Former smoker, no longer smokes',
  current: 'Current smoker, ineligible, complete Study Exit',
};

const WORKERS_COMP_YES = 'Yes, screen fail, complete Study Exit';
const EXAM_YES = 'Yes';
const EXAM_BASELINE = 'N/A, baseline exam';

const PAIN_MEDICATION = /gabapentin|pregabalin|neurontin|lyrica|oxycodone|hydrocodone|oxymorphone|hydromorphone|morphine|tramadol|tapentadol|codeine|fentanyl|buprenorphine|methadone|percocet|norco|acetaminophen|tylenol|ibuprofen|advil|motrin|naproxen|aleve|meloxicam|mobic|celecoxib|diclofenac|ketorolac|toradol|indomethacin|etodolac|nabumetone|cyclobenzaprine|flexeril|methocarbamol|robaxin|baclofen|lioresal|tizanidine|zanaflex|duloxetine|cymbalta|lidocaine|prednisone|methylprednisolone|medrol/i;

function asText(value) {
  if (Array.isArray(value)) return value.map((item) => asText(item)).filter(Boolean).join('; ');
  if (value == null || value === false) return '';
  return String(value).trim();
}

function recommendationInput(recommendation, variable) {
  const inputs = recommendation?.inputs;
  if (!Array.isArray(inputs)) return '';
  const wanted = variable.toLowerCase();
  const row = inputs.find((item) => String(item?.variable || '').toLowerCase() === wanted);
  return row ? asText(row.value) : '';
}

function trimNumber(value, places) {
  const number = Number(value);
  if (!Number.isFinite(number)) return '';
  const factor = 10 ** places;
  const rounded = Math.round(number * factor) / factor;
  return String(rounded);
}

function heightInches(value) {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return '';
  const inches = number > 90 ? number / 2.54 : number;
  return trimNumber(inches, 1);
}

function weightPounds(value, heightRaw) {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return '';
  const pounds = Number(heightRaw) > 90 && number < 140 ? number * 2.20462 : number;
  return trimNumber(pounds, 1);
}

function sexLabel(value) {
  const text = asText(value).toUpperCase();
  if (text === 'F' || text === 'FEMALE') return 'Female';
  if (text === 'M' || text === 'MALE') return 'Male';
  return '';
}

function tobaccoHistory(value) {
  const text = asText(value).toUpperCase();
  if (!text) return '';
  if (text === 'YES' || text === 'CURRENT' || text.includes('CURRENT')) return TOBACCO.current;
  if (text.includes('FORMER')) return TOBACCO.former;
  if (text === 'NO' || text === 'NEVER' || text.includes('NEVER') || text.includes('NON')) return TOBACCO.never;
  return '';
}

function isoDate(value) {
  const match = asText(value).match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : '';
}

function isoTime(value) {
  const match = asText(value).match(/T(\d{2}:\d{2})/);
  return match ? match[1] : '';
}

function levelSpan(text) {
  const found = [...asText(text).matchAll(/\b([CTLS])(\d{1,2})\b/gi)].map((match) => (
    `${match[1].toUpperCase()}${match[2]}`
  ));
  if (!found.length) return null;
  return { from: found[0], to: found[found.length - 1] };
}

function regionCode(characteristics, recommendation) {
  const named = asText(characteristics?.region).toUpperCase();
  if (named === 'CERVICAL' || named === 'THORACIC' || named === 'LUMBAR') return named;
  const level = recommendationInput(recommendation, 'Level of intervention').toLowerCase();
  if (level.includes('cervical') || /\bc\d/.test(level)) return 'CERVICAL';
  if (level.includes('thoracic') || /\bt\d/.test(level)) return 'THORACIC';
  if (level.includes('lumbar') || /\bl\d/.test(level) || /\bs\d/.test(level)) return 'LUMBAR';
  return '';
}

function chooseCase(code, text) {
  const deformity = /\bscoliosis\b|\bkyphosis\b|adult spinal deformity|\basd\b/i.test(text);
  if (deformity) {
    return {
      arm: 'Adult Spinal Deformity',
      region: code === 'CERVICAL' ? 'Cervical' : 'Thoracolumbar',
    };
  }
  if (code === 'CERVICAL') return { arm: 'Cervical', region: '' };
  if (code === 'THORACIC' || code === 'LUMBAR') return { arm: 'Thoracolumbar', region: '' };
  return { arm: 'Cervical', region: '' };
}

function historyPrefix(arm) {
  if (arm === 'Cervical') return 'cervical';
  if (arm === 'Thoracolumbar') return 'thoracolumbar';
  return 'adult_spinal_deformity';
}

function symptomPrefix(arm, region) {
  if (arm === 'Cervical' || region === 'Cervical') return 'cervical';
  return 'thoracolumbar';
}

function markDiagnosis(values, key, levels) {
  values[key] = 'Yes';
  if (!levels) return;
  values[`${key}_level_from`] = levels.from;
  values[`${key}_level_to`] = levels.to;
}

function applyDiagnoses(values, arm, text, levels) {
  const blob = text.toLowerCase();
  const prefix = historyPrefix(arm);
  if (prefix === 'adult_spinal_deformity') {
    if (/degenerative scoliosis/.test(blob)) markDiagnosis(values, 'dx.adult_spinal_deformity.degenerative_scoliosis', levels);
    else if (/\bscoliosis\b/.test(blob)) markDiagnosis(values, 'dx.adult_spinal_deformity.scoliosis', levels);
    if (/\blordosis\b/.test(blob) && !/loss of lordosis|decreased lordosis|reduced lordosis/.test(blob)) {
      markDiagnosis(values, 'dx.adult_spinal_deformity.lordosis', levels);
    }
    return;
  }

  if (/stenosis/.test(blob)) markDiagnosis(values, `dx.${prefix}.spinal_stenosis`, levels);
  if (/spondylolysis/.test(blob) && prefix === 'thoracolumbar') {
    markDiagnosis(values, 'dx.thoracolumbar.spondylolysis', levels);
  }
  if (/spondylolisthesis/.test(blob)) markDiagnosis(values, `dx.${prefix}.spondylolisthesis`, levels);
  if (/discogenic|\bddd\b|degenerative disc/.test(blob)) {
    if (prefix === 'cervical') markDiagnosis(values, 'dx.cervical.ddd', levels);
    else if (/grade\s*(i|1)\b/.test(blob)) markDiagnosis(values, 'dx.thoracolumbar.ddd_up_to_grade_1_spondylolisthesis', levels);
    else markDiagnosis(values, 'dx.thoracolumbar.ddd_unspecified_spondylolisthesis', levels);
  }
  if (/pseudoarthrosis|non-?union|failed previous fusion/.test(blob)) {
    const key = prefix === 'cervical' ? 'dx.cervical.pseudoarthrosis' : 'dx.thoracolumbar.failed_previous_fusion';
    markDiagnosis(values, key, levels);
  }
  if (/\btrauma\b|fracture|dislocation/.test(blob)) markDiagnosis(values, `dx.${prefix}.trauma`, levels);
  if (/\btumor\b|neoplasm/.test(blob)) {
    const key = prefix === 'cervical' ? 'dx.cervical.tumors' : 'dx.thoracolumbar.neoplastic_disease';
    markDiagnosis(values, key, levels);
  }
}

function previousTreatment(clinical, profile, recommendation) {
  const raw = asText(
    clinical?.previous_spine_surgeries
    || recommendationInput(recommendation, 'Number of previous spine surgeries'),
  ).toLowerCase();
  if (raw === '0' || raw === 'none' || raw === 'no') return 'No';
  const count = parseInt(raw, 10);
  if (Number.isFinite(count)) return count > 0 ? 'Yes' : 'No';
  if (Number(profile?.revision_surgery) > 0) return 'Yes';
  if (/yes|prior|previous/.test(raw)) return 'Yes';
  return '';
}

function sideLabel(text) {
  const blob = text.toLowerCase();
  const right = /\bright\b/.test(blob);
  const left = /\bleft\b/.test(blob);
  if (/\bbilateral\b|\bboth sides\b/.test(blob) || (right && left)) return 'Both';
  if (right) return 'Right';
  if (left) return 'Left';
  return '';
}

function applySymptoms(values, prefix, text) {
  const blob = text.toLowerCase();
  const side = sideLabel(blob);
  const present = [];
  const add = (key, pattern) => {
    if (!pattern.test(blob)) return;
    values[`${key}_presence`] = 'Present';
    if (side) values[`${key}_location`] = side;
    present.push(key);
  };
  if (prefix === 'cervical') {
    add('sx.cervical.neck_pain', /neck pain/);
    add('sx.cervical.shoulder_pain', /shoulder pain/);
    add('sx.cervical.upper_extremity_pain', /arm pain|upper extremity pain|radiculopathy/);
    add('sx.cervical.upper_extremity_numbness', /numbness|paresthesia/);
    add('sx.cervical.upper_extremity_weakness', /weakness/);
  } else {
    add('sx.thoracolumbar.back_pain', /back pain|low back|lumbar pain/);
    add('sx.thoracolumbar.lower_extremity_pain', /leg pain|sciatica|radiculopathy/);
    add('sx.thoracolumbar.lower_extremity_numbness', /numbness|paresthesia/);
    add('sx.thoracolumbar.lower_extremity_weakness', /weakness/);
  }
  if (!present.length) return;
  values[`sx.${prefix}.review_performed`] = EXAM_YES;
  values[`sx.${prefix}.new_or_worse`] = EXAM_BASELINE;
}

function labeledScore(text, label) {
  const match = text.match(new RegExp(`${label}[^\\d]{0,24}(\\d+(?:\\.\\d+)?)`, 'i'));
  return match ? match[1] : '';
}

function applyScores(values, text, examDate) {
  const ndi = labeledScore(text, 'ndi');
  const odi = labeledScore(text, 'odi');
  const neck = labeledScore(text, 'vas\\s*neck|neck\\s*vas');
  const arm = labeledScore(text, 'vas\\s*arm|arm\\s*vas');
  const back = labeledScore(text, 'vas\\s*back|back\\s*vas');
  const leg = labeledScore(text, 'vas\\s*leg|leg\\s*vas');
  if (ndi) values['prom.ndi.total'] = ndi;
  if (odi) values['prom.odi.total'] = odi;
  if (neck) values['prom.vas_neck.score'] = neck;
  if (arm) values['prom.vas_arm.score'] = arm;
  if (back) values['prom.vas_back.score'] = back;
  if (leg) values['prom.vas_leg.score'] = leg;
  if (!examDate) return;
  if (ndi) values['prom.ndi.completion_date'] = examDate;
  if (odi) values['prom.odi.completion_date'] = examDate;
  if (neck) values['prom.vas_neck.completion_date'] = examDate;
  if (arm) values['prom.vas_arm.completion_date'] = examDate;
  if (back) values['prom.vas_back.completion_date'] = examDate;
  if (leg) values['prom.vas_leg.completion_date'] = examDate;
}

function procedureBlob(characteristics, recommendation) {
  const parts = [];
  const suggested = characteristics?.suggested_surgeries;
  if (Array.isArray(suggested)) parts.push(...suggested.map(asText));
  parts.push(recommendationInput(recommendation, 'Type of intervention'));
  parts.push(asText(recommendation?.reason_coding_code));
  const surgery = recommendation?.parsed_output?.surgery;
  if (Array.isArray(surgery)) {
    surgery.forEach((item) => parts.push(asText(item?.procedure)));
  }
  return parts.filter(Boolean).join(' ').toLowerCase().replace(/_/g, ' ');
}

function applyApproach(values, arm, region, procedures, code) {
  if (!procedures) return;
  const cervical = arm === 'Cervical' || region === 'Cervical';
  if (cervical && arm !== 'Adult Spinal Deformity') {
    if (/acdf|anterior cervical discectomy/.test(procedures)) {
      values['approach.cervical'] = CERVICAL_APPROACHES.acdf;
    } else if (/laminectomy/.test(procedures) && /fusion|psf/.test(procedures)) {
      values['approach.cervical'] = CERVICAL_APPROACHES.laminectomyFusion;
    } else if (/occipit/.test(procedures)) {
      values['approach.cervical'] = CERVICAL_APPROACHES.occiput;
    } else if (/\bpsf\b|posterior/.test(procedures)) {
      values['approach.cervical'] = CERVICAL_APPROACHES.posterior;
    } else if (/laminoplasty/.test(procedures)) {
      values['approach.cervical'] = CERVICAL_APPROACHES.other;
      values['approach.cervical_other'] = 'Laminoplasty';
    }
    return;
  }
  if (arm === 'Adult Spinal Deformity') {
    if (/percutaneous/.test(procedures) && /\bposterior\b|\bpsf\b/.test(procedures)) {
      values['approach.asd.percutaneous_posterior'] = true;
    } else if (/\bposterior\b|\bpsf\b|\btlif\b|\bplif\b/.test(procedures)) {
      values['approach.asd.direct_posterior'] = true;
    }
    if (/\balif\b|direct anterior/.test(procedures)) values['approach.asd.direct_anterior'] = true;
    if (/\bllif\b|\bxlif\b|direct lateral|\blateral\b/.test(procedures)) values['approach.asd.direct_lateral'] = true;
    if (/\bolif\b|anterior oblique/.test(procedures)) values['approach.asd.anterior_oblique'] = true;
    return;
  }
  const thoracic = code === 'THORACIC';
  if (!thoracic) {
    if (/\bposterior\b|\bpsf\b|\btlif\b|\bplif\b/.test(procedures)) values['approach.lumbar.posterior'] = true;
    if (/\balif\b|direct anterior/.test(procedures)) values['approach.lumbar.direct_anterior'] = true;
    if (/\bllif\b|\bxlif\b|\banterolateral\b|\blateral\b/.test(procedures)) values['approach.lumbar.anterolateral'] = true;
    if (/\bolif\b|anterior-oblique|anterior oblique/.test(procedures)) values['approach.lumbar.anterior_oblique'] = true;
    if (/\batp\b|anterior to psoas/.test(procedures)) values['approach.lumbar.anterior_to_psoas'] = true;
  }
  if (/costotransversectomy/.test(procedures)) values['approach.thoracic.costotransversectomy'] = true;
  if (/transpedicular/.test(procedures)) values['approach.thoracic.transpedicular'] = true;
  if (/transpleural/.test(procedures)) values['approach.thoracic.thoracotomy_transpleural'] = true;
  if (/retropleural/.test(procedures)) values['approach.thoracic.thoracotomy_retropleural'] = true;
  if (/lateral extracavitary/.test(procedures)) values['approach.thoracic.lateral_extracavitary'] = true;
}

function doseParts(value) {
  const match = asText(value).match(/(\d+(?:\.\d+)?)\s*(mg|mcg|µg|ml|g)\b/i);
  if (!match) return {};
  const raw = match[2].toLowerCase();
  const units = raw === 'mcg' || raw === 'µg' ? 'µg' : (raw === 'ml' ? 'mL' : raw);
  return { dose: match[1], dose_units: units };
}

function frequencyLabel(value) {
  const text = asText(value).toLowerCase();
  if (/\bprn\b|as needed/.test(text)) return 'PRN as needed';
  if (/\bqid\b/.test(text)) return 'QID four times a day';
  if (/\btid\b/.test(text)) return 'TID three times a day';
  if (/\bbid\b/.test(text)) return 'BID twice a day';
  if (/\bqd\b|once daily|daily|every day/.test(text)) return 'QD every day';
  if (/\bonce\b/.test(text)) return 'Once';
  return '';
}

function routeLabel(value) {
  const text = asText(value).toLowerCase();
  if (/\bpo\b|oral|by mouth/.test(text)) return 'PO';
  if (/\biv\b/.test(text)) return 'IV';
  if (/topical/.test(text)) return 'TOP';
  if (/\bim\b/.test(text)) return 'IM';
  return '';
}

function medicationRows(patient) {
  const list = patient?.medications;
  if (!Array.isArray(list)) return [];
  const rows = [];
  list.forEach((item) => {
    const key = asText(item?.key || item?.name);
    const value = item?.value;
    const blob = `${key} ${asText(value)}`;
    if (!PAIN_MEDICATION.test(blob)) return;
    const name = PAIN_MEDICATION.test(key) ? key : asText(value) || key;
    const dose = doseParts(value);
    const frequency = frequencyLabel(value);
    const route = routeLabel(value);
    rows.push({
      name: name.replace(/_/g, ' '),
      ...dose,
      ...(frequency ? { frequency } : {}),
      ...(route ? { route } : {}),
    });
  });
  return rows.slice(0, 8);
}

function withProfileDemographics(patient, profile) {
  let phi = patient?.phi;
  if (typeof phi === 'string') {
    try {
      phi = JSON.parse(phi);
    } catch (error) {
      phi = {};
    }
  }
  if (!phi || typeof phi !== 'object') phi = {};
  phi = { ...phi };
  if (!phi.Sex && !phi.sex && profile?.sex) phi.Sex = profile.sex;
  if (!phi.Ethnicity && !phi.ethnicity && profile?.ethnicity) phi.Ethnicity = profile.ethnicity;
  if (!phi.Race && !phi.race && profile?.race) phi.Race = profile.race;
  if (!phi.DOB && !phi.dob && profile?.dob) phi.DOB = profile.dob;
  return { ...patient, phi };
}

function subjectValues(patient) {
  const externalId = asText(patient?.external_id);
  const match = externalId.match(/^(\d{2})-(\d{3,})$/);
  if (!match) return {};
  return {
    site_id: match[1],
    subject_number: match[2],
    subject_id: externalId,
  };
}

function mergeChartFacts(chart, applied) {
  if (!applied?.compass) return chart;
  const forms = { ...chart.forms };
  Object.entries(applied.compass.forms || {}).forEach(([key, form]) => {
    if (key === 'meds::case' && applied.painMedicationCount) {
      forms[key] = form;
      return;
    }
    const prior = forms[key] || { values: {}, repeats: {} };
    forms[key] = {
      values: { ...prior.values, ...(form.values || {}) },
      repeats: { ...prior.repeats, ...(form.repeats || {}) },
    };
  });
  return {
    ...chart,
    forms,
    visits: { ...(chart.visits || {}), ...(applied.compass.visits || {}) },
  };
}

function putForm(forms, formId, visitId, values, repeats) {
  const filled = Object.entries(values).filter(([, value]) => value !== '' && value != null);
  if (!filled.length && !repeats) return;
  forms[`${formId}::${visitId || 'case'}`] = {
    values: Object.fromEntries(filled),
    repeats: repeats || {},
  };
}

export function prefillCompassChart({
  patient = {},
  characteristics = null,
  recommendation = null,
  chartFacts = null,
} = {}) {
  const profile = characteristics?.patient_profile || {};
  const clinical = characteristics?.clinical || {};
  const diagnoses = [
    asText(clinical.diagnoses),
    recommendationInput(recommendation, 'Diagnosis'),
    ...(chartFacts?.diagnoses || []).map((row) => asText(row?.description)),
  ].filter(Boolean).join('; ');
  const chief = asText(clinical.chief_complaint) || recommendationInput(recommendation, 'Chief complaint preop');
  const narrative = [diagnoses, chief].filter(Boolean).join('. ');
  const code = regionCode(characteristics, recommendation);
  const { arm, region } = chooseCase(code, narrative);
  const levels = levelSpan([
    recommendationInput(recommendation, 'Level of intervention'),
    asText(recommendation?.reason_coding_code),
    diagnoses,
  ].join(' '));
  const examDate = isoDate(clinical.exam_date);
  const surgeryDate = isoDate(
    (Array.isArray(recommendation?.parsed_output?.surgery) && recommendation.parsed_output.surgery[0]?.date)
    || patient?.surgery_start_time,
  );
  const surgeryTime = isoTime(patient?.surgery_start_time);

  const demographics = {};
  if (profile.age != null && profile.age !== '') demographics.age_at_surgery = trimNumber(profile.age, 0);
  const sex = sexLabel(profile.sex);
  if (sex) demographics.sex = sex;
  const height = heightInches(profile.height);
  const weight = weightPounds(profile.weight, profile.height);
  const bmi = profile.bmi != null && profile.bmi !== '' ? trimNumber(profile.bmi, 1) : '';
  if (height) demographics.height_inches = height;
  if (weight) demographics.weight_pounds = weight;
  if (bmi) demographics.bmi = bmi;

  const tobacco = {};
  const smoking = tobaccoHistory(profile.smoking);
  if (smoking) tobacco.tobacco_history = smoking;

  const compensation = {};
  const insurance = asText(profile.insurance).toLowerCase();
  if (insurance === 'workers_comp') compensation.receiving_workers_compensation = WORKERS_COMP_YES;
  else if (insurance === 'phi' || insurance === 'auto') compensation.receiving_workers_compensation = 'No';

  const history = {};
  applyDiagnoses(history, arm, diagnoses, levels);
  const prior = previousTreatment(clinical, profile, recommendation);
  if (prior) history[`hx.${historyPrefix(arm)}.previous_treatment`] = prior;

  const symptoms = {};
  applySymptoms(symptoms, symptomPrefix(arm, region), narrative);
  if (examDate && symptoms[`sx.${symptomPrefix(arm, region)}.review_performed`]) {
    symptoms[`sx.${symptomPrefix(arm, region)}.review_date`] = examDate;
  }

  const proms = {};
  applyScores(proms, narrative, examDate);

  const operative = {};
  if (surgeryDate) operative.surgery_date = surgeryDate;
  applyApproach(operative, arm, region, procedureBlob(characteristics, recommendation), code);

  const procedure = {};
  if (surgeryTime) procedure.surgery_start_time = surgeryTime;

  const exam = {};
  if (examDate) exam.physical_exam_completed = EXAM_YES;

  const medications = medicationRows(patient);
  const medValues = {};
  medications.forEach((row, index) => {
    medValues[`med.medication_name#${index}`] = row.name;
    if (row.dose) medValues[`med.dose#${index}`] = row.dose;
    if (row.dose_units) medValues[`med.dose_units#${index}`] = row.dose_units;
    if (row.frequency) medValues[`med.frequency#${index}`] = row.frequency;
    if (row.route) medValues[`med.route#${index}`] = row.route;
  });

  if (medications.length) {
    exam.pain_med_for_spine_last_30_days = 'Yes, record medications on the concomitant medication log';
  }

  const forms = {};
  putForm(forms, 'subject', '', subjectValues(patient));
  putForm(forms, 'demographics', 'pre-op', demographics);
  putForm(forms, 'tobacco', 'pre-op', tobacco);
  putForm(forms, 'compensation', 'pre-op', compensation);
  putForm(forms, 'history', 'pre-op', history);
  putForm(forms, 'symptoms', 'pre-op', symptoms);
  putForm(forms, 'proms', 'pre-op', proms);
  putForm(forms, 'exam', 'pre-op', exam);
  putForm(forms, 'operative', '', operative);
  putForm(forms, 'procedure', '', procedure);
  putForm(
    forms,
    'meds',
    '',
    medValues,
    medications.length > 1 ? { concomitant_medication: medications.length } : null,
  );

  const procedureText = [
    procedureBlob(characteristics, recommendation),
    recommendationInput(recommendation, 'Level of intervention'),
    asText(chartFacts?.procedure_text),
  ].filter(Boolean).join(' ');
  const applied = applyChartFacts(mergePatientDemographics(chartFacts, withProfileDemographics(patient, profile)), {
    arm,
    region,
    procedureText,
    indexDate: surgeryDate,
    indexTime: surgeryTime,
  });

  return mergeChartFacts({
    arm,
    region,
    active: { phaseId: 'preop', visitId: 'pre-op', formId: 'demographics' },
    forms,
    visits: examDate ? { 'pre-op': { date: examDate } } : {},
  }, applied);
}
